import {useTranslation} from "react-i18next";
import type {InsightTileV4} from "../../models/crm-v4";
import type {StatCardData} from "../../kit/StatCard";
import {StatGrid} from "../../kit/StatGrid";
import type {InsightsLoading} from "../../kit/insightsLoading";
import {
    formatDeltaMagnitude,
    formatStatValue,
    reasonMessage,
    unitLabel,
    type InsightStatMetricDefinition,
} from "../base/insightStatCardFormatting";

interface DemographicsStatCardsProps {
    tiles?: InsightTileV4[];
    loading?: InsightsLoading;
    comparing?: boolean;
    /** The rail's search, when one is open: a card whose title does not match is not rendered. */
    matches?: (title: string, key: string) => boolean;
}

export const DEMOGRAPHICS_STAT_METRICS: InsightStatMetricDefinition[] = [
    {id: "totalGuests", title: "Total guests", hint: "Guests booked on accommodation stays arriving in the selected period", format: "NUMBER", fractionDigits: 0},
    {id: "averageAge", title: "Average age", hint: "Average age of the guests who stayed in the selected period, from the birth dates on file", format: "NUMBER", fractionDigits: 0},
    {id: "averagePartySize", title: "Average party size", hint: "Average guests per accommodation booking", format: "NUMBER", fractionDigits: 1},
    {id: "averageStaysPerGuest", title: "Average stays per guest", hint: "Average lifetime accommodation stays per guest among guests who arrived in the selected period", format: "NUMBER", fractionDigits: 1},
    {id: "internationalShare", title: "International guests", hint: "Share of guests whose home country is not the property's", format: "PERCENT"},
    {id: "infantShare", title: "Infants", hint: "Share of guests booked in the property's infant age category", format: "PERCENT"},
    {id: "childrenShare", title: "Children", hint: "Share of guests booked in the property's child age category", format: "PERCENT"},
    {id: "teenShare", title: "Teenagers", hint: "Share of guests booked in the property's teenager age category", format: "PERCENT"},
    {id: "adultsShare", title: "Adults", hint: "Share of guests booked in the property's adult age category", format: "PERCENT"},
    {id: "repeatedGuests", title: "Returning guests", hint: "Guests who arrived in this period and had stayed at the property before", format: "NUMBER", fractionDigits: 0},
    {id: "repeatRate", title: "Repeat rate", hint: "Share of this period's guests who had stayed before", format: "PERCENT"},
    {id: "firstTimeGuests", title: "First-time guests", hint: "Guests for whom this period was their first stay", format: "NUMBER", fractionDigits: 0},
];

export function DemographicsStatCards({tiles, loading, comparing = false, matches}: DemographicsStatCardsProps) {
    const {t} = useTranslation();

    const items: StatCardData[] = DEMOGRAPHICS_STAT_METRICS
        .filter(definition => matches == null || matches(t(definition.title), definition.id))
        .map(definition => ({definition, tile: tiles?.find(item => item.key === definition.id)}))
        .filter(({tile}) => tile?.reason !== "moduleNotActive")
        .map(({definition, tile}) => {
            const unavailable = tile ? reasonMessage(tile.reason, tile.coverage) : undefined;
            const unit = unitLabel(tile?.unit);
            const formatValue = (value: number | null | undefined) => {
                if (!tile) return "—";

                const formatted = formatStatValue(tile.format, value, definition.fractionDigits);
                return value != null && unit ? `${formatted} ${t(unit)}` : formatted;
            };
            const comparison = tile?.comparison;

            return {
                id: definition.id,
                title: t(definition.title),
                hint: unavailable ? `${t(definition.hint)} — ${t(unavailable)}` : t(definition.hint),
                value: formatValue(tile?.value),
                comparison: comparison ? {
                    // The API judges each metric's direction, so the chip does not re-derive it.
                    tone: comparison.tone,
                    trend: comparison.trend ?? "neutral",
                    change: formatDeltaMagnitude(comparison.delta, comparison.kind),
                    previous: formatValue(tile?.compareValue ?? null),
                } : comparing ? {
                    // Comparing against a period with nothing in it still earns a chip: a card with no
                    // chip at all reads as "not comparing", and the panel is where the blank is explained.
                    trend: "neutral" as const,
                    previous: formatValue(tile?.compareValue ?? null),
                } : undefined,
            };
        });

    return <StatGrid items={items} loading={loading}/>;
}

export default DemographicsStatCards;
